/**
 * Machine Setup Types
 * Type definitions for the Machine Setup wizard feature (HIVE-6665)
 */

// ── Entities ────────────────────────────────────────────────────────────────

export interface WizardAttributeDef {
  attrId: number;
  attributeName: string;
  defaultValue: string;
}

export interface MachineType {
  key: string;
  displayName: string;
  machineTypeId: number;
  machineModelId: number;
  /** Broad category for wizard first step: "charger" | "kiosk" */
  category: string;
  /** Behavioral groups: "ev_charger" | "public_ev" | "duo_ev" | "has_evse_current" | "has_duo_evse_current" */
  groups?: string[];
  /** Effective static attribute values keyed by DeviceAttributeId string. Used to pre-fill wizard dynamic fields. */
  staticAttributeDefaults?: Record<string, string>;
  /** Admin-configured attributes shown in the wizard for user editing. */
  wizardAttributes?: WizardAttributeDef[];
}

export interface DeviceSuggestion {
  /** Hardware serial number (e.g. 9517…) — entered in the serial field */
  deviceSerial: string;
  /** Board serial (P12… format) — shown as subtitle in suggestions */
  boardSerial?: string | null;
}

export interface MachineGroupInfo {
  machineGroupId: number;
  machineGroupName: string;
}

export interface AttributeMismatch {
  deviceAttributeId: number;
  attributeName: string | null;
  expectedValue: string;
  expectedCodeId: number | null;
  actualValue: string;
}

export interface ApplyAttributeItem {
  deviceAttributeId: number;
  value: string;
  codeId?: number | null;
}

// ── Wizard state ─────────────────────────────────────────────────────────────

export enum MachineSetupStep {
  MachineType = 0,
  MachineDetails = 1,
  Configuration = 2,
  Review = 3,
  Result = 4,
}

export interface WizardData {
  machineTypeKey: string;
  machineTypeName: string;
  machineName: string;
  actorId: number;        // the actor to create the machine under (may be a location)
  operatorActorId: number; // operator-level actor for machine group lookups
  actorName: string;
  serialNumber: string;
  // Configuration step
  machineGroupId: number;
  machineGroupName: string;
  defaultCredit: string;
  maxCurrentEvse1: string;
  maxCurrentEvse2: string;
  tariffId: string;
  // Language (attr 7105) — EV charger types
  defaultLanguage: string;
  // Charger-level max current (attr 7015) — all EV charger types
  chargerMaxCurrent: string;
  // Connector 1 — all EV charger types
  connectorFormat1: string;   // attr 5710, code: 1=Cable, 2=Socket
  socketType1: string;        // attr 5711, code: 5=Type1, 10=Type2, 40=CCS2, 45=CHAdeMO
  noOfPhases1: string;        // attr 5713, code: 1=1-phase, 3=3-phase
  chargingType1: string;      // attr 5714, code: 1=AC, 2=DC
  maxPowerKw1: string;        // attr 5715, numeric string
  minCurrentEvse1: string;    // attr 5717, numeric string
  // Connector 2 — Duo only
  connectorFormat2: string;   // attr 5720
  socketType2: string;        // attr 5721
  noOfPhases2: string;        // attr 5723
  chargingType2: string;      // attr 5724
  maxPowerKw2: string;        // attr 5725
  minCurrentEvse2: string;    // attr 5727
  // Admin-configured wizard-visible attributes; keyed by DeviceAttributeId string
  customAttributeValues: Record<string, string>;
}

// ── API Requests ─────────────────────────────────────────────────────────────

export interface CreateMachineRequest {
  machineName: string;
  machineTypeKey: string;
  actorId: number;
  machineGroupId?: number;
  serialNumber?: string;
  defaultCredit?: string;
  maxCurrentEvse1?: string;
  maxCurrentEvse2?: string;
  tariffId?: string;
  defaultLanguage?: string;
  chargerMaxCurrent?: string;
  connectorFormat1?: string;
  socketType1?: string;
  noOfPhases1?: string;
  chargingType1?: string;
  maxPowerKw1?: string;
  minCurrentEvse1?: string;
  connectorFormat2?: string;
  socketType2?: string;
  noOfPhases2?: string;
  chargingType2?: string;
  maxPowerKw2?: string;
  minCurrentEvse2?: string;
  customAttributeValues?: Record<string, string>;
}

export interface MachineGroupsResponse {
  isSuccess: boolean;
  message: string | null;
  machineGroups: MachineGroupInfo[];
}

export interface ValidateSerialRequest {
  serialNumber: string;
}

// ── API Responses (match back-office ApiResponseBase<T> JSON shape) ──────────

export interface MachineTypesResponse {
  isSuccess: boolean;
  message: string | null;
  machineTypes: MachineType[];
}

export interface SerialValidationResponse {
  isSuccess: boolean;
  message: string | null;
  deviceFound: boolean;
  isAssigned: boolean;
  serialNumber: string;
  isWrongDistributor?: boolean;
  deviceActorId?: number | null;
}

export interface CreateMachineResponse {
  isSuccess: boolean;
  message: string | null;
  machineId: number | null;
  machineName: string | null;
  warning?: string | null;
}

export interface ValidateMachineConfigResponse {
  isSuccess: boolean;
  message: string | null;
  machineId: number;
  machineTypeKey: string;
  configMatches: boolean;
  mismatches: AttributeMismatch[];
}

// ── Machine Type Settings (admin) ─────────────────────────────────────────────

export interface MachineTypeSettingsResponse {
  isSuccess: boolean;
  errorMessage: string | null;
  types: MachineTypeSettingDto[];
  attributeNames: Record<string, string>;
  /** All known attribute categories keyed by DeviceAttributeId string. Used to auto-categorize new rows. */
  attributeCategories: Record<string, string>;
}

export interface MachineTypeSettingDto {
  key: string;
  displayName: string;
  hasOverrides: boolean;
  isCustom: boolean;
  hardwareModel?: string | null;
  machineTypeId: number;
  machineModelId: number;
  attributes: PresetAttributeViewDto[];
}

export interface PresetAttributeViewDto {
  deviceAttributeId: number;
  attributeName: string;
  category: string;
  value: string;
  codeId?: number | null;
  wizardVisible?: boolean;
}

export interface SaveTypeSettingsRequest {
  attributes: PersistedPresetAttribute[];
  hardwareModel?: string | null;
  machineTypeId?: number | null;
  machineModelId?: number | null;
  displayName?: string | null;
}

export interface PersistedPresetAttribute {
  deviceAttributeId: number;
  value: string;
  codeId?: number | null;
  wizardVisible?: boolean;
}

export interface AddCustomMachineTypeRequest {
  key: string;
  displayName: string;
  machineTypeId: number;
  machineModelId: number;
  category: string;
  hardwareModel?: string;
}

export interface LynxMachineInfoResponse {
  isSuccess: boolean;
  message: string | null;
  machineId: number | null;
  machineName: string | null;
  machineTypeId: number | null;
  machineModelId: number | null;
  /** DeviceFamily from the linked Nayax device (e.g. "VPOSM4"). Null if no device is linked. */
  deviceFamily: string | null;
  /** Board serial (P12… format) from the linked device. */
  boardSerial: string | null;
}

export interface CreateThirdPartyMachineRequest {
  machineName: string;
  actorId: number;
  machineGroupId?: number;
  chargerIdNumber: string;
  manufacturerId?: number | null;
}

/** Slim manufacturer info needed for third-party selection — matches DCS ManufacturerItem fields */
export interface ThirdPartyManufacturer {
  id: number;
  manufacturerName: string;
  modelName?: string | null;
  isDc: boolean;
  isOcppTariff: boolean;
  isOcppCost: boolean;
}

// ── Bulk Create ───────────────────────────────────────────────────────────────

export interface BulkCreateMachineRequest extends CreateMachineRequest {
  count: number;
  baseName: string;
}

export interface BulkCreateMachineResult {
  machineName: string;
  machineId: number | null;
  isSuccess: boolean;
  error?: string | null;
  warning?: string | null;
}

export interface BulkCreateMachineResponse {
  isSuccess: boolean;
  message: string | null;
  results: BulkCreateMachineResult[];
}
